import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { History, CheckCircle, XCircle, ClockIcon, RefreshCw, Copy } from 'lucide-react';

const WithdrawalHistory = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [userId, setUserId] = useState('');
  const [userSponsorId, setUserSponsorId] = useState('');

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedSponsorId = localStorage.getItem('userSponsorId');
    if (storedUserId) {
      setUserId(storedUserId);
      setUserSponsorId(storedSponsorId || storedUserId);
      fetchWithdrawalHistory(storedUserId, storedSponsorId || storedUserId);
    }
  }, []);

  const fetchWithdrawalHistory = async (userIdToUse, sponsorIdToUse) => {
    try {
      setLoading(true);
      const withdrawalsRef = ref(database, 'withdrawals');
      const snapshot = await get(withdrawalsRef);

      const allWithdrawals = [];
      
      if (snapshot.exists()) {
        const data = snapshot.val();

        // Loop through all user groups so edited/admin-updated nodes are never missed
        Object.entries(data).forEach(([date, dateWithdrawals]) => {
          if (!dateWithdrawals || typeof dateWithdrawals !== 'object') return;

          Object.entries(dateWithdrawals).forEach(([groupKey, userGroup]) => {
            if (!userGroup || typeof userGroup !== 'object') return;

            Object.entries(userGroup).forEach(([id, withdrawal]) => {
              if (!withdrawal || typeof withdrawal !== 'object') return;

              const withdrawalUserId = String(withdrawal.userId || groupKey || '');
              const withdrawalSponsorId = String(withdrawal.sponsorId || '');
              const belongsToUser =
                withdrawalUserId === String(userIdToUse) ||
                withdrawalUserId === String(sponsorIdToUse) ||
                withdrawalSponsorId === String(userIdToUse) ||
                withdrawalSponsorId === String(sponsorIdToUse) ||
                String(groupKey) === String(userIdToUse) ||
                String(groupKey) === String(sponsorIdToUse);

              if (!belongsToUser) return;

              allWithdrawals.push({
                id,
                date,
                ...withdrawal
              });
            });
          });
        });
      }

      // Sort by date (newest first)
      allWithdrawals.sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));
      
      setWithdrawals(allWithdrawals);
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    switch(normalizedStatus) {
      case 'approved':
      case 'Approved':
        return { icon: <CheckCircle size={14} />, color: 'bg-green-500/20 text-green-400 border-green-500/30' };
      case 'rejected':
      case 'Rejected':
        return { icon: <XCircle size={14} />, color: 'bg-red-500/20 text-red-400 border-red-500/30' };
      case 'pending':
      case 'Pending':
        return { icon: <ClockIcon size={14} />, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
      default:
        return { icon: <ClockIcon size={14} />, color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
    }
  };

  const getWalletType = (type) => {
    switch(type) {
      case 'working': return 'Working Wallet';
      case 'roi': return 'ROI Wallet';
      case 'energy': return 'Energy Wallet';
      default: return 'Wallet';
    }
  };

  const filteredWithdrawals =
    filter === 'all'
      ? withdrawals
      : withdrawals.filter((w) => String(w.status || '').toLowerCase() === filter.toLowerCase());

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 pt-20 pb-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
            <p className="mt-3 text-sm text-muted-foreground">Loading withdrawal history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pt-16 pb-8">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-foreground">Withdrawal History</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Track all your withdrawal requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-card p-3 rounded-lg text-center">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="text-base font-semibold text-foreground">{withdrawals.length}</div>
        </div>
        <div className="bg-card p-3 rounded-lg text-center">
          <div className="text-xs text-muted-foreground">Pending</div>
          <div className="text-base font-semibold text-yellow-400">
            {withdrawals.filter(w => String(w.status || '').toLowerCase() === 'pending').length}
          </div>
        </div>
        <div className="bg-card p-3 rounded-lg text-center">
          <div className="text-xs text-muted-foreground">Approved</div>
          <div className="text-base font-semibold text-green-400">
            {withdrawals.filter(w => String(w.status || '').toLowerCase() === 'approved').length}
          </div>
        </div>
      </div>

      {/* Filters and Refresh */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setFilter('all')}
            className={`px-2.5 py-1.5 rounded-lg text-xs ${filter === 'all' ? 'bg-primary text-white' : 'bg-card text-muted-foreground'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-2.5 py-1.5 rounded-lg text-xs ${filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-card text-muted-foreground'}`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-2.5 py-1.5 rounded-lg text-xs ${filter === 'approved' ? 'bg-green-600 text-white' : 'bg-card text-muted-foreground'}`}
          >
            Approved
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-2.5 py-1.5 rounded-lg text-xs ${filter === 'rejected' ? 'bg-red-600 text-white' : 'bg-card text-muted-foreground'}`}
          >
            Rejected
          </button>
        </div>
        <button
          onClick={() => fetchWithdrawalHistory(userId, userSponsorId || userId)}
          className="ml-auto px-2.5 py-1.5 bg-card text-muted-foreground rounded-lg flex items-center gap-1 text-xs"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Withdrawal List */}
      <div className="space-y-3">
        {filteredWithdrawals.length === 0 ? (
          <div className="text-center py-10 bg-card rounded-xl">
            <History className="mx-auto mb-3 text-muted-foreground" size={32} />
            <h3 className="text-sm font-semibold text-foreground mb-1">No Withdrawals</h3>
            <p className="text-xs text-muted-foreground">
              {filter === 'all' 
                ? 'You have no withdrawal requests yet.' 
                : `No ${filter} withdrawals found.`}
            </p>
          </div>
        ) : (
          filteredWithdrawals.map((withdrawal) => {
            const statusBadge = getStatusBadge(withdrawal.status);
            const displayAddress =
              withdrawal?.crypto?.address ||
              withdrawal?.address ||
              withdrawal?.newAddress ||
              'N/A';
            const displayCurrency =
              withdrawal?.crypto?.currency ||
              withdrawal?.currency ||
              'N/A';
            const displayNetwork =
              withdrawal?.crypto?.network ||
              withdrawal?.network ||
              'N/A';
            
            return (
              <div key={withdrawal.id} className="bg-card rounded-xl p-3.5">
                {/* Header */}
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className="text-sm font-semibold text-foreground">{formatCurrency(withdrawal.amount)}</div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border ${statusBadge.color}`}>
                        {statusBadge.icon}
                        {withdrawal.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {getWalletType(withdrawal.walletType)} • {displayCurrency}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">{formatDate(withdrawal.requestDate)}</div>
                    <div className="text-[10px] text-muted-foreground/70">ID: {withdrawal.id.slice(0, 8)}</div>
                    {withdrawal.processedDate && (
                      <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                        Processed: {formatDate(withdrawal.processedDate)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div>
                    <div className="text-muted-foreground">Net Amount</div>
                    <div className="text-green-400 font-semibold">{formatCurrency(withdrawal.netAmount)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Fee ({withdrawal.withdrawalFee}%)</div>
                    <div className="text-red-400">{formatCurrency(withdrawal.feeAmount)}</div>
                  </div>
                </div>

                {/* Address */}
                <div className="pt-2 border-t border-border">
                  <div className="text-muted-foreground text-xs mb-1">Crypto Details</div>
                  <div className="grid grid-cols-2 gap-1 text-xs mb-1">
                    <div className="text-muted-foreground">Currency:</div>
                    <div className="text-foreground">{displayCurrency}</div>
                    <div className="text-muted-foreground">Network:</div>
                    <div className="text-foreground">{displayNetwork}</div>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <div className="text-xs text-muted-foreground flex-1">
                      <span className="block text-muted-foreground mb-0.5">Address:</span>
                      <span className="text-foreground text-[11px] break-all">{displayAddress}</span>
                    </div>
                    {displayAddress && displayAddress !== 'N/A' && (
                      <button
                        onClick={() => copyToClipboard(displayAddress)}
                        className="p-1.5 rounded bg-card-hover hover:bg-card-hover/80 text-muted-foreground shrink-0 mt-4"
                        title="Copy Address"
                      >
                        <Copy size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WithdrawalHistory;